/**
 * @fileoverview Shared job status labels and ordering.
 *
 * The Convex `jobStatusValidator` is the source of truth for valid values;
 * this module gives the frontend a single place to attach human-readable
 * labels, badge variants, and a stable display order for selectors and
 * filter pills. Keeps the four status-aware UIs (sidebar header, dashboard
 * home, jobs list, job settings) in lockstep with each other.
 */

import type { JobStatus } from "@cvx/jobs/schema";

/** Display order used by selects, filter pills, and tag pickers. */
export const JOB_STATUS_ORDER: ReadonlyArray<JobStatus> = [
  "pre_appointment",
  "day_one",
  "active",
  "on_hold",
  "archived",
  "completed",
  "did_not_proceed",
];

/**
 * Lenient string-keyed lookup so call sites that hold `job.status` as a
 * generic `string` (from Convex query result objects) can read labels
 * without casting. Unknown values fall through and the call site falls
 * back to the raw status string.
 */
export const JOB_STATUS_LABELS: Record<string, string> = {
  pre_appointment: "Pre Appointment",
  day_one: "Day One",
  active: "Active",
  on_hold: "On Hold",
  archived: "Archived",
  completed: "Completed",
  did_not_proceed: "Did Not Proceed",
};

/**
 * Variant lookup for the shared `Badge` component. New statuses default to
 * sensible existing variants — refine these once design has bespoke colours.
 */
export const JOB_STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  pre_appointment: "outline",
  day_one: "default",
  active: "default",
  on_hold: "secondary",
  archived: "outline",
  completed: "default",
  did_not_proceed: "destructive",
};

/** Convenience array of `{ value, label }` pairs for `<Select>` rendering. */
export const JOB_STATUS_OPTIONS: ReadonlyArray<{
  value: JobStatus;
  label: string;
}> = JOB_STATUS_ORDER.map((value) => ({
  value,
  label: JOB_STATUS_LABELS[value],
}));
